"use client";

import {createContext, useContext, useEffect, useRef, useState, type ReactNode} from "react";

import {usePathname} from "next/navigation";

function randomId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, "0")).join("");
}

const OnlineContext = createContext<number | null>(null);
const storageKey = "sports-online-visitor-v1";
function visitorId(): string {
  const existing = localStorage.getItem(storageKey);
  if (existing && /^[a-f0-9]{32}$/.test(existing)) return existing;
  const id = randomId();
  localStorage.setItem(storageKey, id);
  return id;
}

export function OnlineVisitorsProvider({children}: {children: ReactNode}) {
  const [count, setCount] = useState<number | null>(null);
  const pathname = usePathname();
  const pageRef = useRef<{path: string; eventId: string; sent: boolean} | null>(null);
  useEffect(() => {
    if (pathname && pageRef.current?.path !== pathname) {
      pageRef.current = {path: pathname, eventId: randomId(), sent: false};
    }
    let disposed = false;
    let pending = false;
    let controller: AbortController | undefined;
    async function heartbeat() {
      if (pending || document.visibilityState !== "visible") return;
      pending = true;
      controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 10000);
      try {
        const id = visitorId();
        const page = pageRef.current;
        const pageEvent = page && !page.sent ? {eventId: page.eventId, path: page.path} : undefined;
        const response = await fetch("/api/online", {
          method: "POST", headers: {"Content-Type": "application/json"},
          body: JSON.stringify({id, page: pageEvent}), cache: "no-store", signal: controller.signal,
        });
        if (!response.ok) throw new Error("Online unavailable");
        const data: unknown = await response.json();
        if (pageEvent && page && pageRef.current === page) page.sent = true;
        if (!disposed && typeof data === "object" && data !== null && "count" in data &&
            typeof data.count === "number" && Number.isInteger(data.count) && data.count >= 0) {
          setCount(data.count);
        }
      } catch {
        // Не показываем устаревшее число при недоступности сервиса.
        if (!disposed) setCount(null);
      } finally { clearTimeout(timeout); pending = false; }
    }
    void heartbeat();
    const timer = setInterval(() => void heartbeat(), 30000);
    const onVisible = () => { if (document.visibilityState === "visible") void heartbeat(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      disposed = true; clearInterval(timer); controller?.abort();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pathname]);
  return <OnlineContext.Provider value={count}>{children}</OnlineContext.Provider>;
}

export default function OnlineVisitors({dashboard = false}: {dashboard?: boolean}) {
  const count = useContext(OnlineContext);
  if (dashboard) return (
    <section className="glass-card rounded-2xl p-5">
      <h2 className="text-lg font-black uppercase tracking-wide text-white">Сейчас на сайте</h2>
      <p className="mt-2 text-3xl font-black text-emerald-300">{count ?? "—"}</p>
      <p className="mt-2 text-xs text-emerald-200/60">Посетители, активные за последние 2 минуты</p>
    </section>
  );
  return <span className="no-print inline-flex items-center gap-2 whitespace-nowrap text-emerald-200/60">
    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-400" />
    Сейчас на сайте: <strong className="tabular-nums text-emerald-200">{count ?? "—"}</strong>
  </span>;
}
